<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import type {
  AdminUser,
  AdminUserCleanupCandidate,
  AdminUserOptions,
  SystemRoleCode,
} from '@/types/domain';
import { errorMessage, formatDate } from '@/utils/format';
import { groupOrgUnits } from '@/utils/org-units';
import { confirmAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import RecordHistory from '@/components/RecordHistory.vue';
import UsageDashboard from '@/components/UsageDashboard.vue';
import OperationsPanel from '@/components/OperationsPanel.vue';

interface UserDraft {
  username: string;
  displayName: string;
  email: string;
  mobile: string;
  company: string;
  /** 归属工厂；'' = 不登记，按钉钉部门推断（0923） */
  factory: string;
  orgUnitId: number | null;
  /** 直属主管：变更审批「申请人直属部门经理」那一级按它派人（0828） */
  leaderUserId: number | null;
  status: 'active' | 'inactive';
  loginEnabled: boolean;
  roles: SystemRoleCode[];
  projectIds: number[];
  temporaryPassword: string;
}

const users = ref<AdminUser[]>([]);
const options = ref<AdminUserOptions>({ roles: [], orgUnits: [], projects: [] });
const selectedId = ref<number | null>(null);
const creating = ref(false);
const loading = ref(false);
const saving = ref(false);
const passwordSaving = ref(false);
const error = ref('');
/** 保存失败的原因，写在「保存」按钮旁边（0924：只写在页顶，人看不见） */
const saveError = ref('');
/** 用户清单 / 使用情况看板 */
const view = ref<'list' | 'import' | 'usage' | 'cleanup' | 'operations'>('list');

/*
  整理用户名单（0828 Tony：「我在清理用户名单，这些停用的是不是有部分是部门，
  本身就不应该有账户，另外是迁移过来已经废弃的」）。

  分堆的判据在后端，是「这个账号还被系统里的谁引用」——
  界面只负责把三堆摆清楚，并且**不许勾选 blocked 那一堆**：
  勾不动比勾了再报错好，能不能动是后端算出来的事实，不该让人先点一下才知道。
*/
const cleanupLoading = ref(false);
const cleanupRunning = ref(false);
const cleanupDone = ref('');
const cleanupPicked = ref<Set<number>>(new Set());
const cleanup = ref<{
  org: AdminUserCleanupCandidate[];
  stale: AdminUserCleanupCandidate[];
  blocked: AdminUserCleanupCandidate[];
}>({ org: [], stale: [], blocked: [] });

const cleanupGroups = computed(() => [
  {
    kind: 'org' as const,
    title: '部门误建的行 —— 本来就不该有账号',
    why: '旧 EQS 迁过来时把部门当成人导进了用户表。部门表里有正牌记录，删掉这些行不丢任何东西。',
    items: cleanup.value.org,
  },
  {
    kind: 'stale' as const,
    title: '迁移过来的废弃账号 —— 谁都没引用',
    why: '系统里没有任何地方用到他们。归档＝名字留着备查、不再占名单，不是删除。',
    items: cleanup.value.stale,
  },
  {
    kind: 'blocked' as const,
    title: '还挂着东西 —— 先别动',
    why: '这些账号还被项目团队、交付物、审批记录之类引用着。动了，那些地方就只剩一个查不到的数字 ID。',
    items: cleanup.value.blocked,
  },
]);

async function loadCleanup(): Promise<void> {
  cleanupLoading.value = true;
  cleanupDone.value = '';
  try {
    cleanup.value = await eqsApi.adminUserCleanupCandidates();
    // 重新清点后旧的勾选可能已经不成立了，清掉重来
    cleanupPicked.value = new Set();
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
  } finally {
    cleanupLoading.value = false;
  }
}

function openCleanup(): void {
  view.value = 'cleanup';
  if (!cleanup.value.org.length && !cleanup.value.stale.length && !cleanup.value.blocked.length) {
    void loadCleanup();
  }
}

function togglePick(id: number): void {
  const next = new Set(cleanupPicked.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  cleanupPicked.value = next;
}

function toggleGroup(items: AdminUserCleanupCandidate[], on: boolean): void {
  const next = new Set(cleanupPicked.value);
  for (const item of items) {
    if (on) next.add(item.id);
    else next.delete(item.id);
  }
  cleanupPicked.value = next;
}

async function runCleanup(): Promise<void> {
  const picked = [...cleanupPicked.value];
  const ok = await confirmAction({
    title: '执行整理',
    message: `选中 ${picked.length} 个：部门误建的行会删掉，废弃账号会归档（名字留着备查）。继续？`,
  });
  if (!ok) return;
  cleanupRunning.value = true;
  try {
    const result = await eqsApi.runAdminUserCleanup(picked);
    const parts: string[] = [];
    if (result.removed.length) parts.push(`删掉部门误建 ${result.removed.length} 行`);
    if (result.archived.length) parts.push(`归档废弃账号 ${result.archived.length} 个`);
    // 被拒的要点名，不能只报个数——他得知道是谁、卡在哪
    if (result.refused.length) {
      parts.push(`拒绝 ${result.refused.length} 个（${result.refused.map((item) => `${item.name}：${item.references.join('、')}`).join('；')}）`);
    }
    // 先刷新再写提示：loadCleanup 会清掉提示，顺序反了这行字一闪就没
    await loadCleanup();
    await loadUsers();
    cleanupDone.value = parts.length ? parts.join('；') : '没有可处理的项';
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
  } finally {
    cleanupRunning.value = false;
  }
}

/*
  组织同步（钉钉 → PDM）。

  接口 `POST /org-sync/run` 一直都在，但**界面上从来没有入口**，
  Tony 0822 找了一圈没找到（「模块中心的组织没找到」）。
  岗位就是这条同步灌进来的，所以入口放在用户管理这一页最顺手。
  先跑 dryRun 看清单，再决定要不要真写——组织数据动一次影响所有人的可见范围。
*/
const syncing = ref(false);
const syncReport = ref<string>('');
/** 上一次同步新建了哪些账号；报出来让管理员知道多了谁 */
const syncCreated = ref<Array<{ jobNumber: string; name: string; dept: string }>>([]);

/**
 * 点名哪些部门「连没账号的人一起拉进来并建号」（0827 Tony：
 * 「同步后能否把研发部门没有的人员自动新增账户」）。
 *
 * 接口一直支持，但界面从来没传过——于是同步永远只认已有账号的人，
 * 新入职的项目工程师同步多少次都出不来。默认给研发中心：
 * 那是项目工程师、前期质量工程师所在的部门，也是这次找不到人的那个部门。
 * 建出来的账号**不能登录、不带角色**，只为了在选人下拉里出得来；
 * 要让谁真能登录，在这一页单独开——那是另一个决定。
 */
const createDepts = ref('研发中心');

/** 输入框里逗号/顿号/换行分隔的部门名 */
function deptPaths(): string[] {
  return createDepts.value.split(/[,，、\s]+/u).map((item) => item.trim()).filter(Boolean);
}

async function runOrgSync(dryRun: boolean): Promise<void> {
  if (syncing.value) return;
  const paths = deptPaths();
  if (!dryRun) {
    const ok = await confirmAction({
      title: '确认从钉钉同步组织架构？',
      message: '会更新部门、上级和岗位；不改角色和密码。'
        + (paths.length
          ? `点名的部门（${paths.join('、')}）里没有账号的人会**新建账号**——不能登录、不带角色，只为能被选到。`
          : '没点名部门，所以不会新建任何账号。'),
      confirmLabel: '开始同步',
    });
    if (!ok) return;
  }
  syncing.value = true;
  syncReport.value = '';
  syncCreated.value = [];
  try {
    /*
      报告要按后端真实返回的字段念（0827）。
      原来读的是 departments/people/updated/created 这几个键——后端从来没返回过，
      于是这一行永远只显示「同步完成」，跑没跑出东西完全看不出来。
    */
    const r = await eqsApi.runOrgSync(dryRun, paths) as unknown as {
      departmentsCreated?: number; departmentsUpdated?: number;
      peopleMatched?: number; leadersLinked?: number;
      peopleCreated?: Array<{ jobNumber: string; name: string; dept: string }>;
      peopleMoved?: unknown[]; titlesChanged?: unknown[]; unmatchedPeople?: string[];
      emailsFilled?: unknown[];
    };
    syncCreated.value = r.peopleCreated ?? [];
    syncReport.value = [
      dryRun ? '试算结果（没有写库）' : '同步完成',
      `部门 新建 ${r.departmentsCreated ?? 0} / 更新 ${r.departmentsUpdated ?? 0}`,
      `人员匹配 ${r.peopleMatched ?? 0}`,
      `${dryRun ? '将新建' : '新建'}账号 ${syncCreated.value.length}`,
      // 补邮箱要单独报：补上了他们才收得到系统邮件（0827）
      (r.emailsFilled ?? []).length ? `${dryRun ? '将补' : '补上'}邮箱 ${(r.emailsFilled ?? []).length} 人` : '',
      `换部门 ${(r.peopleMoved ?? []).length}`,
      `岗位变动 ${(r.titlesChanged ?? []).length}`,
      `连主管 ${r.leadersLinked ?? 0}`,
      // 对不上的人要报出来，不能让人以为全同步到了
      (r.unmatchedPeople ?? []).length ? `对不上 ${(r.unmatchedPeople ?? []).length} 人` : '',
    ].filter(Boolean).join(' · ');
    if (!dryRun) await loadUsers();
  } catch (caught) {
    syncReport.value = '';
    error.value = errorMessage(caught);
  } finally {
    syncing.value = false;
  }
}
const success = ref('');
const resetPassword = ref('');
/** 重置那一块展开没有；原来靠 <details>，入口看不出能点（0828） */
const resetOpen = ref(false);
const total = ref(0);
const pageSize = 50;

const filters = reactive({
  search: '',
  status: '',
  role: '',
  orgUnitId: '',
  loginEnabled: '',
  factory: '',
  page: 1,
});

/** 收进「筛选」里的条件有几个在生效：收着的时候也得看得出名单是筛过的（1027 micro2） */
const moreFilterCount = computed(() =>
  [filters.status, filters.role, filters.orgUnitId, filters.factory, filters.loginEnabled].filter(Boolean).length);

/** 厂的叫法：后端给的 label 优先（「JQAT饰件科技（奉化）」），认不出原样显示 */
function factoryName(value: string | null | undefined): string {
  if (!value) return '未归属';
  return options.value.factories?.find((item) => item.value === value)?.label ?? value;
}

/*
  主管候选（0828 Tony:「这个下拉也不准啊，能否只有高管可以在下拉，我下拉都没看到肖娟」）。

  两处都错了：① 从「当前这一页的用户」里挑——一页 50 人，肖娟在后面，下拉里没有她；
  ② 115 个人全列出来，既选不动也选不对。
  现在由后端一次给全**管理岗**（含项目主管、各中心经理/总监/董事长），
  外加已经是别人主管的人。本人排掉——自己不能当自己的主管，否则审批往上派会死循环。
*/
const leaderCandidates = computed(() =>
  (options.value.leaderCandidates ?? []).filter((person) => person.id !== selectedId.value));

/*
  组织下拉按主体分组、按层级缩进（0923 Tony:「组织下拉里面很乱」）。
  筛选那个标人数（含下级，和筛选口径同一套）；编辑用户那个不标——选的是「归到哪」，不是「有几个人」。
*/
const orgGroupsWithCount = computed(() => groupOrgUnits(options.value.orgUnits, true));
const orgGroups = computed(() => groupOrgUnits(options.value.orgUnits, false));

const draft = reactive<UserDraft>(blankDraft());
const selected = computed(() =>
  users.value.find((user) => user.id === selectedId.value) ?? null,
);
// 项目经理和项目成员一样按项目限定可见范围；只有管理员和只读审计员才是「看全部」
const projectScopeApplies = computed(() =>
  (draft.roles.includes('member') || draft.roles.includes('project_manager'))
  && !draft.roles.includes('admin')
  && !draft.roles.includes('readonly'),
);
const pageCount = computed(() => Math.max(1, Math.ceil(total.value / pageSize)));

function blankDraft(): UserDraft {
  return {
    username: '',
    displayName: '',
    email: '',
    mobile: '',
    company: '',
    factory: '',
    orgUnitId: null,
    leaderUserId: null,
    status: 'active',
    loginEnabled: false,
    roles: ['member'],
    projectIds: [],
    temporaryPassword: '',
  };
}

function clearMessages(): void {
  error.value = '';
  saveError.value = '';
  success.value = '';
}

function useDraft(source: UserDraft): void {
  Object.assign(draft, source);
}

function selectUser(user: AdminUser): void {
  creating.value = false;
  selectedId.value = user.id;
  resetPassword.value = '';
  clearMessages();
  // 换人时把重置那块收回去：上一个人的展开态和没提交的密码不该跟过来（0828）
  resetOpen.value = false;
  resetPassword.value = '';
  useDraft({
    username: user.username || '',
    displayName: user.displayName,
    email: user.email || '',
    mobile: user.mobile || '',
    company: user.company || '',
    factory: user.factory || '',
    orgUnitId: user.orgUnitId,
    leaderUserId: user.leaderUserId,
    status: user.status,
    loginEnabled: user.loginEnabled,
    roles: [...user.roles],
    projectIds: [...user.projectIds],
    temporaryPassword: '',
  });
  void nextTick(() => document.querySelector('.user-editor')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }));
}

async function newUser(): Promise<void> {
  view.value = 'list';
  creating.value = true;
  selectedId.value = null;
  openedPassword.value = null;
  resetPassword.value = '';
  clearMessages();
  useDraft(blankDraft());
  await nextTick();
  const input = document.querySelector<HTMLInputElement>('.user-editor input');
  input?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  input?.focus({ preventScroll: true });
}

const pendingActivation = computed(() => filters.status === 'active' && filters.loginEnabled === 'false');

async function openPeople(pending: boolean): Promise<void> {
  view.value = 'list';
  creating.value = false;
  selectedId.value = null;
  users.value = [];
  total.value = 0;
  openedPassword.value = null;
  resetPassword.value = '';
  resetOpen.value = false;
  clearMessages();
  Object.assign(filters, { search: '', status: pending ? 'active' : '', role: '', orgUnitId: '', loginEnabled: pending ? 'false' : '', factory: '', page: 1 });
  await loadUsers();
  await nextTick();
  document.querySelector('.user-list-heading')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
}

function queryString(): string {
  const params = new URLSearchParams();
  if (filters.search.trim()) params.set('search', filters.search.trim());
  if (filters.status) params.set('status', filters.status);
  if (filters.role) params.set('role', filters.role);
  if (filters.orgUnitId) params.set('orgUnitId', filters.orgUnitId);
  if (filters.loginEnabled) params.set('loginEnabled', filters.loginEnabled);
  if (filters.factory) params.set('factory', filters.factory);
  params.set('page', String(filters.page));
  params.set('pageSize', String(pageSize));
  return params.toString();
}

async function loadUsers(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.adminUsers(queryString());
    users.value = payload.items;
    total.value = payload.total;
    if (
      !creating.value
      && selectedId.value !== null
      && !users.value.some((user) => user.id === selectedId.value)
    ) {
      selectedId.value = null;
    }
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

async function applyFilters(): Promise<void> {
  filters.page = 1;
  await loadUsers();
}

async function movePage(next: number): Promise<void> {
  filters.page = Math.min(pageCount.value, Math.max(1, next));
  await loadUsers();
}

function normalizedBasicPayload(): Record<string, unknown> {
  return {
    username: draft.username.trim(),
    displayName: draft.displayName.trim(),
    email: draft.email.trim() || null,
    mobile: draft.mobile.trim() || null,
    company: draft.company.trim() || null,
    // 不登记就送 null：撤掉登记、回到按钉钉部门推断
    factory: draft.factory || null,
    orgUnitId: draft.orgUnitId,
    leaderUserId: draft.leaderUserId,
    status: draft.status,
    loginEnabled: draft.status === 'active' && draft.loginEnabled,
  };
}

async function save(): Promise<void> {
  clearMessages();
  const wasCreating = creating.value;
  if (!draft.username.trim() || !draft.displayName.trim()) {
    error.value = '用户名和姓名不能为空。';
    return;
  }
  if (!draft.roles.length) {
    error.value = '至少选择一个角色。';
    return;
  }
  if (creating.value && draft.loginEnabled && draft.temporaryPassword.length < 10) {
    error.value = '启用新账号时，请设置至少 10 位的临时密码。';
    return;
  }

  saving.value = true;
  try {
    let saved: AdminUser;
    if (creating.value) {
      saved = await eqsApi.createAdminUser({
        ...normalizedBasicPayload(),
        roles: draft.roles,
        projectIds: draft.projectIds,
        ...(draft.temporaryPassword
          ? { temporaryPassword: draft.temporaryPassword }
          : {}),
      });
    } else if (selectedId.value !== null) {
      saved = await eqsApi.updateAdminUser(selectedId.value, {
        ...normalizedBasicPayload(),
        roles: draft.roles,
        projectIds: draft.projectIds,
      });
    } else {
      return;
    }
    creating.value = false;
    selectedId.value = saved.id;
    await loadUsers();
    const refreshed = users.value.find((user) => user.id === saved.id) ?? saved;
    selectUser(refreshed);
    success.value = wasCreating
      ? '用户已新增，角色和项目范围已生效。'
      : '用户资料、角色和项目范围已保存。';
  } catch (caught) {
    /*
      0924 Tony:「保存之后还是显示之前信息」。失败原因只写在页面最上面那条提示里，
      离表单里的「保存」按钮一屏远、还是一句英文校验报错——看起来就像存了没变。
      现在按钮旁边也写一遍，再弹一条通知。
    */
    error.value = errorMessage(caught);
    saveError.value = error.value;
    notify('error', '没保存上', error.value);
  } finally {
    saving.value = false;
  }
}

/**
 * 一键开通登录（0827 Tony：「直接把他们允许登入，发邮件给他们，
 * 随机生成一个登录密码，第一次登入改一下密码」）。
 *
 * 密码由服务端生成并直接发到本人邮箱，**不经过这个界面**——
 * 管理员屏幕上不该出现别人的密码。只有那个人没有可用邮箱时才回来一串，
 * 那时候如实显示并说明是"没发出去，请你转交"。
 */
const openedPassword = ref<{ name: string; password: string } | null>(null);

async function openLogin(): Promise<void> {
  if (selectedId.value === null || !selected.value) return;
  clearMessages();
  openedPassword.value = null;
  const who = selected.value.displayName || selected.value.username || '这个账号';
  if (!await confirmAction({
    title: `给 ${who} 开通登录？`,
    message: '会随机生成一个临时密码并邮件发给本人，首次登录强制改密码；'
      + '同时撤销他现有的登录会话。没有可用邮箱时密码会显示出来，由你转交。',
    confirmLabel: '开通并发邮件',
  })) return;
  passwordSaving.value = true;
  try {
    const r = await eqsApi.openAdminUserLogin(selectedId.value);
    if (r.delivered === 'email') {
      success.value = `已开通并把临时密码发到 ${r.email}，对方首次登录必须改密码。`;
    } else {
      // 发不出去要说清楚为什么，并把密码摆出来让人转交——不能只说"成功了"
      openedPassword.value = { name: who, password: r.password ?? '' };
      success.value = '已开通，但这个账号没有可用邮箱，密码没发出去——请把下面这串转交给本人。';
    }
    await loadUsers();
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    passwordSaving.value = false;
  }
}

async function setTemporaryPassword(): Promise<void> {
  if (selectedId.value === null) return;
  clearMessages();
  if (resetPassword.value.length < 10) {
    error.value = '临时密码至少需要 10 位。';
    return;
  }
  if (!await confirmAction({ title: '确认重置该用户的密码并撤销其现有登录会话？', danger: true })) return;
  passwordSaving.value = true;
  try {
    await eqsApi.setAdminUserTemporaryPassword(
      selectedId.value,
      resetPassword.value,
    );
    resetPassword.value = '';
    resetOpen.value = false;
    success.value = '临时密码已设置，用户下次登录后必须修改密码。';
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    passwordSaving.value = false;
  }
}

function roleName(code: SystemRoleCode): string {
  return options.value.roles.find((role) => role.code === code)?.name
    // 后端没给名字时的兜底。少一种就会在界面上露出英文角色码（0828 补了 director）
    ?? ({
      admin: '系统管理员',
      director: '项目总监',
      project_manager: '项目经理',
      member: '项目成员',
      readonly: '只读审计员',
    }[code] ?? code);
}

function formatTime(value: string | null): string {
  if (!value) return '从未登录';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('zh-CN', { hour12: false });
}

/*
  重算业务指标（0916 从「大屏数据」模块搬过来）。

  那个模块整体撤掉了——它只有一张和指挥台同源的异常清单加这个按钮，
  而全库只有 1 条操作留痕、最近一次 2026-08-05。按钮本身还有用：
  dashboard-refresh 每 30 分钟自动重算一次，卡住的时候这是唯一的手动出口
  （大屏进度停在 8/5 那次就是这么发现的）。

  放在用户管理页：侧栏里「用户管理 / 操作留痕」就是管理员的运维区，
  这类动作归在这儿比藏在一个叫「大屏数据」的模块里好找。
*/
const recalculating = ref(false);
const recalculatedAt = ref('');

async function recalculateDashboard(): Promise<void> {
  if (!await confirmAction({
    title: '现在立刻重算一次业务指标？',
    message: '系统每 30 分钟已自动重算一次。这里只是不等下一轮，立刻按当前业务事实重算。',
  })) return;
  recalculating.value = true;
  try {
    const result = await eqsApi.recalculateDashboard();
    recalculatedAt.value = String(result.recalculatedAt || '');
    notify('success', '业务指标已重算', `完成时间：${formatDate(recalculatedAt.value)}`);
  } catch (caught) {
    notify('error', '重算失败', errorMessage(caught));
  } finally {
    recalculating.value = false;
  }
}

onMounted(async () => {
  loading.value = true;
  try {
    const [metadata] = await Promise.all([
      eqsApi.adminUserOptions(),
      loadUsers(),
    ]);
    options.value = metadata;
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section class="page-heading">
    <div>
      <span>USER MANAGEMENT</span>
      <h1>用户管理</h1>
      <p>维护用户资料、登录状态、系统角色和项目可见范围。停用账号不会删除历史记录。</p>
    </div>
    <div class="user-head-actions">
      <!-- 使用情况看板：推行到哪一步了，看这里比看用户清单快（0821 Tony） -->
      <nav class="user-view-tabs" aria-label="用户管理视图">
        <button type="button" :class="{ on: view === 'list' }" @click="view = 'list'">用户清单</button>
        <button type="button" :class="{ on: view === 'usage' }" @click="view = 'usage'">使用情况</button>
        <button type="button" :class="{ on: view === 'operations' }" @click="view = 'operations'">运行检查</button>
        <!-- 名单里混着部门和废弃账号，专门给一个视图收拾（0828 Tony） -->
        <button type="button" :class="{ on: view === 'cleanup' }" @click="openCleanup">整理名单</button>
      </nav>
    </div>
  </section>

  <!-- 三个入口压成一行矮按钮（1027）：原来三张大卡片占掉半屏，用户清单被挤到首屏以下。说明一行省略，全文在 title -->
  <section v-if="view === 'list' || view === 'import'" class="people-entry" aria-label="人员管理快捷入口">
    <button type="button" class="people-entry-card" title="单独添加一人，填写资料、角色和项目范围。" @click="newUser">
      <strong>＋ 新增人员</strong><span>单独添加一人，填写资料、角色和项目范围。</span><b>填写新增资料 →</b>
    </button>
    <button type="button" class="people-entry-card" :class="{ on: view === 'import' }" :aria-pressed="view === 'import'" title="按部门添加人员，先查看试算结果，再确认同步。" @click="view = 'import'">
      <strong>钉钉导入</strong><span>按部门添加人员，先查看试算结果，再确认同步。</span><b>选择导入部门 →</b>
    </button>
    <button type="button" class="people-entry-card" :class="{ on: view === 'list' && pendingActivation }" :aria-pressed="view === 'list' && pendingActivation" title="查找已有但尚不能登录的人员，核对后逐人开通。" @click="openPeople(true)">
      <strong>待开通人员</strong><span>查找已有但尚不能登录的人员，核对后逐人开通。</span><b>查看待开通名单 →</b>
    </button>
  </section>

  <section v-if="view === 'import'" class="panel people-import" aria-label="钉钉导入">
    <h2>从钉钉添加人员</h2>
    <p>填写要添加人员的部门，先试算查看结果，再确认同步。同步会更新部门、上级和岗位；新增账号需管理员另行开通登录并设置角色。</p>
    <div class="people-import-actions">
      <label class="user-sync-depts" title="这些部门（含子部门）里没有 PDM 账号的人，同步时给他建一个（不能登录、不带角色，只为能在选人下拉里出得来）。留空＝不建任何账号。">
        <span>建号部门</span>
        <input v-model="createDepts" type="text" placeholder="留空＝不建账号">
      </label>
      <button class="button" type="button" :disabled="syncing" @click="runOrgSync(true)">
        {{ syncing ? '同步中…' : '试算组织同步' }}
      </button>
      <button class="button" type="button" :disabled="syncing" @click="runOrgSync(false)">
        ⟳ 从钉钉同步组织
      </button>
    </div>
    <p class="field-note">多个部门可用逗号分隔；留空只同步已有人员的组织信息，不新增人员。</p>
    <p v-if="syncReport" class="user-sync-report">{{ syncReport }}</p>
    <!-- 新建了谁要逐个点名：多出几个账号是管理员该知道的事，不能只报个数字 -->
    <p v-if="syncCreated.length" class="user-sync-created">
      新建账号（不能登录、不带角色）：
      <b v-for="item in syncCreated" :key="item.jobNumber">{{ item.name }}（{{ item.jobNumber }} · {{ item.dept }}）</b>
    </p>
    <button type="button" class="button" @click="openPeople(true)">同步后，查看待开通人员 →</button>
  </section>

  <UsageDashboard :active="view === 'usage'" />
  <OperationsPanel :active="view === 'operations'" />

  <!--
    整理名单（0828 Tony：「这些停用的是不是有部分是部门，本身就不应该有账户，
    另外是迁移过来已经废弃的」）。
    分三堆，判据是「还被谁引用」——不是名字像不像部门。
  -->
  <section v-if="view === 'cleanup'" class="panel user-cleanup">
    <header class="panel-head">
      <div>
        <h2>整理用户名单</h2>
        <p>
          按「还被系统里的谁引用」分堆。部门误建的行删掉（部门表里有正牌记录，删了不丢东西）；
          废弃账号归档（名字留着备查，不再占名单）；还挂着东西的一律不动。
        </p>
      </div>
      <button class="button" type="button" :disabled="cleanupLoading" @click="loadCleanup">
        {{ cleanupLoading ? '清点中…' : '⟳ 重新清点' }}
      </button>
    </header>

    <p v-if="cleanupDone" class="cleanup-done">{{ cleanupDone }}</p>

    <div v-for="group in cleanupGroups" :key="group.kind" class="cleanup-group">
      <h3>
        <label v-if="group.kind !== 'blocked'">
          <input
            type="checkbox"
            :checked="group.items.length > 0 && group.items.every((item) => cleanupPicked.has(item.id))"
            @change="toggleGroup(group.items, ($event.target as HTMLInputElement).checked)"
          >
          {{ group.title }}（{{ group.items.length }}）
        </label>
        <span v-else>{{ group.title }}（{{ group.items.length }}）</span>
      </h3>
      <p class="cleanup-why">{{ group.why }}</p>
      <p v-if="!group.items.length" class="cleanup-empty">这一堆是空的。</p>
      <ul v-else class="cleanup-list">
        <li v-for="item in group.items" :key="item.id">
          <label v-if="group.kind !== 'blocked'">
            <input type="checkbox" :checked="cleanupPicked.has(item.id)" @change="togglePick(item.id)">
            <b>{{ item.name }}</b>
            <span class="cleanup-account">{{ item.username || '无用户名' }} · ID {{ item.id }}</span>
          </label>
          <span v-else>
            <b>{{ item.name }}</b>
            <span class="cleanup-account">{{ item.username || '无用户名' }} · ID {{ item.id }}</span>
            <!-- 拒绝时必须说清卡在哪，只甩一句「不能删」等于让人自己去猜 -->
            <em class="cleanup-refs">还挂着：{{ item.references.join('、') }}</em>
          </span>
        </li>
      </ul>
    </div>

    <footer class="cleanup-foot">
      <span>已选 {{ cleanupPicked.size }} 个</span>
      <button
        class="button button-primary"
        type="button"
        :disabled="!cleanupPicked.size || cleanupRunning"
        @click="runCleanup"
      >
        {{ cleanupRunning ? '处理中…' : '执行整理' }}
      </button>
    </footer>
  </section>

  <div v-if="error" class="inline-alert"><b>操作未完成</b><span>{{ error }}</span></div>
  <div v-if="success" class="user-success"><b>已完成</b><span>{{ success }}</span></div>

  <section v-if="view === 'list' && pendingActivation" class="pending-guide" aria-label="待开通说明">
    <div><b>待开通人员</b><p>这里只显示在用且禁止登录的账号。选中一人，核对角色和项目范围，再到人员详情开通登录。</p></div>
    <button type="button" class="button" @click="openPeople(false)">返回全部人员</button>
  </section>

  <!--
    1027 micro2：手机上五个下拉竖排，把用户表推出首屏。搜索 + 查询常显；
    状态/角色/组织/工厂/登录收进「筛选」，默认收起，有条件生效时 summary 上带个数。
    只是收起来——筛选语义和请求参数不动，下拉一改照旧立刻查。
  -->
  <section v-if="view === 'list'" class="user-filter">
    <label class="user-search">
      <span>搜索用户</span>
      <input
        v-model="filters.search"
        placeholder="用户名、姓名、邮箱或手机"
        @keyup.enter="applyFilters"
      />
    </label>
    <button class="button button-secondary" type="button" @click="applyFilters">查询</button>
    <details class="user-filter-more" :class="{ active: moreFilterCount > 0 }">
      <summary title="状态、角色、组织、归属工厂、登录">筛选<b v-if="moreFilterCount">{{ moreFilterCount }}</b></summary>
      <div class="user-filter-panel">
        <label>
          <span>状态</span>
          <select v-model="filters.status" @change="applyFilters">
            <option value="">全部状态</option>
            <option value="active">在用</option>
            <option value="inactive">已停用</option>
            <!-- 这两类默认不混在名单里，选了才查出来（0828） -->
            <option value="reclassified_org">部门误建（不是人）</option>
            <option value="archived">已归档</option>
          </select>
        </label>
        <label>
          <span>角色</span>
          <select v-model="filters.role" @change="applyFilters">
            <option value="">全部角色</option>
            <option v-for="role in options.roles" :key="role.code" :value="role.code">
              {{ role.name }}
            </option>
          </select>
        </label>
        <label>
          <span>组织</span>
          <select v-model="filters.orgUnitId" @change="applyFilters">
            <option value="">全部组织</option>
            <!-- 标人数（含下级）：选进去才发现是 0，比不列还糟（0827）；按主体分组（0923） -->
            <optgroup v-for="group in orgGroupsWithCount" :key="group.entity" :label="group.title">
              <option v-for="org in group.items" :key="org.id" :value="String(org.id)">{{ org.text }}</option>
            </optgroup>
          </select>
        </label>
        <!-- 按归属工厂筛：登记的优先，没登记按钉钉部门——和审批按厂派人认的是同一句（0923） -->
        <label>
          <span>归属工厂</span>
          <select v-model="filters.factory" @change="applyFilters">
            <option value="">全部</option>
            <option v-for="item in options.factories ?? []" :key="item.value" :value="item.value">{{ item.label }}</option>
            <option value="未归属">未归属（两样都认不出）</option>
          </select>
        </label>
        <label class="login-status">
          <span>登录</span>
          <select v-model="filters.loginEnabled" @change="applyFilters">
            <option value="">全部</option>
            <option value="true">允许登录</option>
            <option value="false">禁止登录</option>
          </select>
        </label>
      </div>
    </details>
  </section>

  <section v-if="view === 'list'" class="user-layout">
    <div class="content-panel user-list-panel">
      <header class="user-list-heading">
        <div><b>用户清单</b><span>共 {{ total }} 人</span></div>
        <small v-if="loading">正在读取…</small>
      </header>
      <div class="user-table-wrap">
        <table class="data-table data-table--rows-link user-table">
          <thead>
            <tr>
              <th>用户</th>
              <th>组织 / 归属工厂</th>
              <!-- 岗位（钉钉里的职位）：审批流要按岗位选人，先得看得见（0822 Tony 第 7 条） -->
              <th>岗位</th>
              <th>角色</th>
              <th class="login-status">登录状态</th>
              <th>项目范围</th>
              <th>最近登录</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="user in users"
              :key="user.id"
              :class="{ 'is-selected': selectedId === user.id }"
              @click="selectUser(user)"
            >
              <td>
                <b>{{ user.displayName }}</b>
                <small>{{ user.username || '无用户名' }} · ID {{ user.id }}</small>
              </td>
              <td>
                <span>{{ user.orgUnitName || '未分配组织' }}</span>
                <!--
                  原来这里小字写「公司」：116 个人全是同一句「宁波东禾智能科技有限公司」，
                  连奉化的人也是，一格信息都没有。换成归属工厂，并说清是登记的还是按部门推的。
                -->
                <small class="factory-line" :class="{ manual: !!user.factory }">
                  {{ factoryName(user.factoryEffective) }}{{ user.factory ? '' : ' · 按钉钉部门' }}
                </small>
              </td>
              <td>
                <span>{{ user.jobTitle || '—' }}</span>
              </td>
              <td>
                <span class="role-line">
                  <i v-for="role in user.roles" :key="role">{{ roleName(role) }}</i>
                </span>
              </td>
              <td class="login-status">
                <span :class="user.status === 'active' && user.loginEnabled ? 'state-ok' : 'state-off'">
                  {{ user.status !== 'active' ? '已停用' : user.loginEnabled ? '允许登录' : '禁止登录' }}
                </span>
              </td>
              <td>
                <span v-if="user.roles.includes('admin') || user.roles.includes('readonly')">全部项目</span>
                <span v-else>{{ user.projectIds.length }} 个项目</span>
                <small>{{ user.projectScopeMode === 'managed' ? '管理员维护' : '沿用项目团队' }}</small>
              </td>
              <td><small>{{ formatTime(user.lastLoginAt) }}</small></td>
            </tr>
            <tr v-if="!loading && !users.length">
              <td colspan="6" class="empty-row">没有符合筛选条件的用户。</td>
            </tr>
          </tbody>
        </table>
      </div>
      <footer class="user-pagination">
        <button
          class="inline-button"
          type="button"
          :disabled="filters.page <= 1"
          @click="movePage(filters.page - 1)"
        >上一页</button>
        <span>第 {{ filters.page }} / {{ pageCount }} 页</span>
        <button
          class="inline-button"
          type="button"
          :disabled="filters.page >= pageCount"
          @click="movePage(filters.page + 1)"
        >下一页</button>
      </footer>
    </div>

    <aside class="content-panel user-editor">
      <header>
        <span>{{ creating ? 'CREATE USER' : selected ? `USER / ${selected.id}` : 'USER DETAILS' }}</span>
        <h2>{{ creating ? '新增用户' : selected?.displayName || '选择一名用户' }}</h2>
        <p v-if="creating">先填写人员资料，再核对角色和项目范围；需要登录时设置临时密码。</p>
        <p v-if="!creating && !selected">从左侧清单选择用户后，可维护资料、角色和项目范围。</p>
      </header>

      <form v-if="creating || selected" @submit.prevent="save">
        <fieldset>
          <legend>基本信息</legend>
          <div class="user-form-grid">
            <label><span>用户名 / 工号 *</span><input v-model="draft.username" required /></label>
            <label><span>姓名 *</span><input v-model="draft.displayName" required /></label>
            <label><span>邮箱</span><input v-model="draft.email" type="email" /></label>
            <label><span>手机</span><input v-model="draft.mobile" /></label>
            <label><span>公司</span><input v-model="draft.company" /></label>
            <!--
              归属工厂（0923 Tony:「员工管理里面可以把各自归属的工厂的信息加进去」）。
              钉钉部门说不全：研发总监、总经理挂在 JQAT 底下却管所有项目；
              给均源、东元干活的项目经理坐在本部研发中心。审批按厂派人认的就是这一格。
            -->
            <label>
              <span>归属工厂</span>
              <select v-model="draft.factory">
                <option value="">不登记（按钉钉部门{{ selected ? `：${factoryName(selected.factoryFromOrg)}` : '推断' }}）</option>
                <option v-for="item in options.factories ?? []" :key="item.value" :value="item.value">{{ item.label }}</option>
              </select>
              <small class="field-hint">同名岗位（如运营中心总监）各厂都有时，按项目经理的归属工厂派人；登记了就不再按钉钉部门推。</small>
            </label>
            <label>
              <span>组织</span>
              <select v-model="draft.orgUnitId">
                <option :value="null">未分配</option>
                <optgroup v-for="group in orgGroups" :key="group.entity" :label="group.title">
                  <option v-for="org in group.items" :key="org.id" :value="org.id">{{ org.text }}</option>
                </optgroup>
              </select>
            </label>
            <!--
              直属主管（0828 Tony:「申请人的直属管理关系也不对」）。
              钉钉只给「部门负责人」，说不出部门内部的层级——项目开发 23 个人
              的主管全是研发中心负责人。变更审批第 2 级按这一格派人，所以要能改。
            -->
            <label>
              <span>直属主管</span>
              <select v-model="draft.leaderUserId">
                <option :value="null">没有主管</option>
                <option v-for="person in leaderCandidates" :key="person.id" :value="person.id">
                  {{ person.displayName }}{{ person.jobTitle ? ` · ${person.jobTitle}` : '' }}
                </option>
              </select>
              <!-- 说明放在框下面：塞进 label 会把这一格撑成两倍高，挤掉旁边的字段（0828 截图） -->
              <small class="field-note">
                变更审批「申请人直属部门经理」那一级按它派人。只列管理岗。
                <template v-if="selected?.leaderSource === 'manual'">当前是人工指定的，钉钉同步不会覆盖。</template>
                <template v-else-if="selected?.leaderName">当前来自钉钉的部门负责人；改过之后同步不再覆盖。</template>
              </small>
            </label>
            <label>
              <span>用户状态 <b class="field-hint">停用=人离职或调岗，账号保留、历史记录留痕；这不是「移出某个项目」，那个在项目团队页做</b></span>
              <select v-model="draft.status">
                <option value="active">在用</option>
                <option value="inactive">停用（保留账号与历史）</option>
              </select>
            </label>
            <label class="check-line">
              <input
                v-model="draft.loginEnabled"
                type="checkbox"
                :disabled="draft.status !== 'active'"
              />
              <span>允许登录系统</span>
            </label>
          </div>
        </fieldset>

        <fieldset>
          <legend>角色与权限</legend>
          <div class="role-options">
            <label v-for="role in options.roles" :key="role.code">
              <input v-model="draft.roles" type="checkbox" :value="role.code" />
              <span><b>{{ role.name }}</b><small>{{ role.description }}</small></span>
            </label>
          </div>
        </fieldset>

        <fieldset>
          <legend>项目可见范围</legend>
          <p v-if="!projectScopeApplies" class="scope-note">
            系统管理员和只读审计员可查看全部项目，项目清单不参与限制。
          </p>
          <div v-else class="project-options">
            <label
              v-for="project in options.projects"
              :key="project.id"
              :class="{ archived: project.archived }"
            >
              <input v-model="draft.projectIds" type="checkbox" :value="project.id" />
              <span><b>{{ project.projectNo }}</b><small>{{ project.projectName }}</small></span>
            </label>
          </div>
          <p v-if="projectScopeApplies && !draft.projectIds.length" class="scope-note">
            当前未选择项目；保存后该用户将看不到任何项目。
          </p>
        </fieldset>

        <fieldset v-if="creating">
          <legend>首次登录</legend>
          <label class="wide-field">
            <span>临时密码{{ draft.loginEnabled ? ' *' : '' }}</span>
            <input
              v-model="draft.temporaryPassword"
              type="password"
              autocomplete="new-password"
              placeholder="至少 10 位；首次登录后必须修改"
            />
          </label>
        </fieldset>

        <button class="button button-primary button-block" type="submit" :disabled="saving">
          {{ saving ? '正在保存…' : creating ? '新增用户' : '保存资料与权限' }}
        </button>
        <p v-if="saveError" class="inline-alert save-error" role="alert"><b>没保存上</b><span>{{ saveError }}</span></p>
      </form>

      <!--
        一键开通（0827）：钉钉同步建出来的账号默认不能登录，
        要一个个手填密码再口头转告，人是不会做的。随机生成 + 直接发到本人邮箱。
      -->
      <div v-if="selected && !creating && !selected.loginEnabled" class="password-open">
        <p>
          <b>这个账号还不能登录</b>
          <span>钉钉同步建出来的账号默认只能被选到，不能登录。</span>
        </p>
        <button
          class="button button-primary button-block" type="button"
          :disabled="passwordSaving" @click="openLogin"
        >{{ passwordSaving ? '正在开通…' : '开通登录并把密码邮件发给本人' }}</button>
      </div>
      <p v-if="openedPassword" class="password-handover">
        <b>{{ openedPassword.name }} 的临时密码：</b><code>{{ openedPassword.password }}</code>
        <span>没有可用邮箱，没发出去。请当面或用别的方式转交，他首次登录后必须改掉。</span>
      </p>

      <!--
        重置临时密码（0828 Tony:「用户管理里面重置密码点击不了」）。

        原来这块是 <details>，入口只有一行加粗红字「重置临时密码」——
        看着像小标题，不像能点的东西。**可点的东西必须有边框**（0827 已经为别处定过
        这条规矩），这里犯的是同一个错。改成和上面「开通登录」一样的按钮。
        另外「至少 10 位」原来是点了才弹的错，现在写在框下面、不够就按钮是灰的。
      -->
      <div v-if="selected && !creating" class="password-reset">
        <button
          class="button button-danger button-block reset-toggle" type="button"
          @click="resetOpen = !resetOpen"
        >{{ resetOpen ? '收起' : '重置临时密码' }}</button>
        <template v-if="resetOpen">
          <p>重置后会撤销该用户现有登录会话，并要求其下次登录后修改密码。</p>
          <label class="wide-field">
            <span>新临时密码</span>
            <input
              v-model="resetPassword"
              type="password"
              autocomplete="new-password"
              placeholder="至少 10 位"
            />
          </label>
          <small class="field-note">
            至少 10 位。<template v-if="resetPassword && resetPassword.length < 10">还差 {{ 10 - resetPassword.length }} 位。</template>
          </small>
          <button
            class="button button-danger button-block"
            type="button"
            :disabled="passwordSaving || resetPassword.length < 10"
            @click="setTemporaryPassword"
          >{{ passwordSaving ? '正在重置…' : '确认重置密码' }}</button>
        </template>
      </div>

      <!--
        改动史（0824 补）。这一页干的全是敏感事：停用账号、收回角色、重置密码——
        「谁把谁停用了」以前在系统里只有「操作留痕」页查得到，
        而那页要求先知道对象 ID，等于查不到。
        两个类型都查：同一张 iam_users 表被两处写入，模块中心记作 user、
        这一页记作 iam_user，只查一个会把历史劈成两半。
      -->
      <RecordHistory
        v-if="selected"
        entity-type="iam_user,user"
        :entity-id="selected.id"
        hint="这个账号的启用/停用、角色和资料改动。"
      />
    </aside>
  </section>

  <!--
    运维区（0916）。撤掉「大屏数据」模块时把它唯一还有用的动作挪到这儿——
    侧栏里「用户管理 / 操作留痕」本来就是管理员的那一块。
    文案要说清「不点也会自己算」，否则管理员会以为不点数字就一直不对：
    之前正是这么以为的，按钮从 8 月 5 日起没人点过。
    1027 挪到清单下面：它不是用户表的一部分，不该夹在入口和清单之间占一行。
  -->
  <section v-if="view === 'list'" class="ops-strip">
    <div>
      <b>运维</b>
      <span>业务指标（大屏、指挥台、项目进度）每 30 分钟自动重算一次。这里只是不等下一轮。</span>
      <small v-if="recalculatedAt">刚刚重算完成：{{ formatDate(recalculatedAt) }}</small>
    </div>
    <button class="button" type="button" :disabled="recalculating" @click="recalculateDashboard">
      {{ recalculating ? '重算中…' : '立即重算业务指标' }}
    </button>
  </section>
</template>

<style scoped>
/* 归属工厂：登记过的着色，按钉钉部门推的保持灰（0923） */
.factory-line.manual { color: #0f766e; font-weight: 600; }
/* 运维区：和用户清单拉开距离，别让人以为它是用户表的一部分 */
.ops-strip {
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  margin-top: 18px; padding: 12px 16px;
  border: 1px solid var(--line); border-left: 3px solid var(--muted); background: var(--paper);
}
.ops-strip b { margin-right: 8px; color: var(--ink); font-size: 13px; }
.ops-strip span { color: var(--muted); font-size: 12px; line-height: 1.7; }
.ops-strip small { display: block; margin-top: 2px; color: var(--green); font-size: 12px; }

.user-head-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
/* 三个入口一行排开、各占一行高（1027）：说明一行省略、全文在 title；按钮有边框就看得出能点，「→」那句不再占位 */
.people-entry { display: flex; flex-wrap: wrap; gap: 8px; margin: 8px 0 12px; }
.people-entry-card { flex: 1 1 220px; min-width: 0; display: flex; align-items: baseline; gap: 8px; padding: 8px 12px; text-align: left; border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--white); color: var(--ink); cursor: pointer; }
.people-entry-card strong { flex: none; font-size: 15px; white-space: nowrap; }
.people-entry-card span { flex: 1 1 auto; min-width: 0; overflow: hidden; color: var(--muted); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.people-entry-card b { display: none; }
.people-entry-card:hover, .people-entry-card.on { border-color: var(--blue); background: rgba(0,117,174,.05); }
.people-entry-card:focus-visible { outline: 3px solid var(--blue); outline-offset: 3px; }
.people-import h2 { margin-top: 0; }.people-import p { line-height: 1.8; }
.people-import-actions { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin: 16px 0; }
.pending-guide { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin: 16px 0; padding: 14px 18px; background: rgba(0,117,174,.06); border-left: 3px solid var(--blue); }
.pending-guide p { margin: 5px 0 0; line-height: 1.7; font-size: 13px; }
/* 一键开通（0827）：钉钉建出来的账号默认不能登录，这块只在那种账号上出现 */
.password-open { margin: 12px 0 0; padding: 10px 12px; border-left: 3px solid var(--blue); background: rgba(0, 117, 174, .06); }
.password-open p { display: grid; gap: 2px; margin: 0 0 8px; font-size: 12px; line-height: 1.6; }
.password-open span { color: var(--muted); }
/* 发不出去时才摆出来的那一串：要显眼，也要说清为什么在这儿 */
.password-handover { margin: 10px 0 0; padding: 10px 12px; border-left: 3px solid var(--warning-graphic); background: #fdf6ec; color: #7a4a10; font-size: 12px; line-height: 1.8; }
.password-handover code { display: inline-block; margin: 0 6px; padding: 2px 8px; background: #fff; border: 1px solid var(--line); border-radius: 4px; font-size: 13px; font-weight: 700; letter-spacing: .5px; }
.password-handover span { display: block; color: #7a4a10; }
.user-sync-depts { display: flex; align-items: center; gap: 6px; }
.user-sync-depts > span { color: var(--muted); font-size: 11px; white-space: nowrap; }
.user-sync-depts input { min-height: 32px; width: 150px; padding: 0 8px; border: 1px solid var(--line); border-radius: 4px; font-size: 12px; }
/* 新建了谁要逐个点名：多出几个账号是管理员该知道的事 */
.user-sync-created { margin: 4px 0 0; color: var(--ink); font-size: 12px; line-height: 1.8; }
.user-sync-created b { margin-right: 10px; font-weight: 700; }
.user-sync-report { margin: 6px 0 0; color: var(--muted); font-size: 12px; }
/* 字段下面的说明：不能塞进 label，会把这一格撑成两倍高（0828 截图里那个方框） */
.field-note { display: block; margin-top: 4px; color: var(--muted); font-size: 11px; line-height: 1.6; }
.user-cleanup .panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.user-cleanup .panel-head p { margin: 4px 0 0; max-width: 70ch; color: var(--muted); font-size: 12px; line-height: 1.7; }
.cleanup-done { margin: 12px 0 0; padding: 8px 12px; border-radius: 4px; background: var(--ok-soft, #eef7ee); color: var(--ink); font-size: 12px; }
.cleanup-group { margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--line); }
.cleanup-group h3 { margin: 0; font-size: 13px; font-weight: 700; }
.cleanup-group h3 label { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; }
.cleanup-why { margin: 4px 0 10px; max-width: 78ch; color: var(--muted); font-size: 12px; line-height: 1.7; }
.cleanup-empty { margin: 0; color: var(--muted); font-size: 12px; }
.cleanup-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 6px 18px; margin: 0; padding: 0; list-style: none; }
.cleanup-list li { font-size: 12px; }
.cleanup-list label { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; }
.cleanup-list b { font-weight: 600; }
.cleanup-account { color: var(--muted); }
.cleanup-refs { display: block; margin-left: 0; color: var(--warn, #a15c00); font-style: normal; }
.cleanup-foot { display: flex; align-items: center; justify-content: flex-end; gap: 14px; margin-top: 22px; padding-top: 16px; border-top: 1px solid var(--line); font-size: 12px; }
.user-view-tabs { display: inline-flex; gap: 4px; padding: 3px; border: 1px solid var(--line); border-radius: 999px; }
.user-view-tabs button { padding: 5px 14px; border: 0; border-radius: 999px; background: transparent; color: var(--muted); font-size: 13px; cursor: pointer; }
.user-view-tabs button.on { background: var(--blue); color: #fff; font-weight: 700; }
.user-success {
  display: flex;
  gap: 14px;
  margin: 14px 0;
  padding: 12px 14px;
  border-left: 4px solid var(--green);
  background: rgba(38, 122, 99, .08);
  font-size: 14px;
}
.user-success b { color: var(--green); white-space: nowrap; }
/* 一行：搜索 + 查询 + 「筛选」；其余条件在「筛选」展开的浮层里，盖在清单上、不推它下去（1027 micro2） */
.user-filter {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 480px) auto auto;
  justify-content: start;
  gap: 10px;
  align-items: end;
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
  color: var(--ink);
  background: var(--white);
}
.user-filter label { display: grid; gap: 6px; }
.user-filter label span { color: var(--muted); font-size: 12px; }
.user-filter input,
.user-filter select {
  width: 100%;
  height: 42px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
  color: var(--ink);
  background: var(--white);
}
.user-filter-more > summary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 42px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
  color: var(--ink);
  background: var(--white);
  font-size: 13px;
  font-weight: 800;
  list-style: none;
  cursor: pointer;
  user-select: none;
}
.user-filter-more > summary::-webkit-details-marker { display: none; }
.user-filter-more.active > summary,
.user-filter-more[open] > summary { border-color: var(--blue); color: var(--blue); }
.user-filter-more > summary b {
  min-width: 18px;
  padding: 0 5px;
  border-radius: 999px;
  color: #fff;
  background: var(--blue);
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}
.user-filter-panel {
  position: absolute;
  z-index: 12;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-s);
  background: var(--white);
  box-shadow: 0 8px 24px rgba(15, 23, 42, .08);
}
.user-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(390px, 470px);
  gap: 18px;
  align-items: start;
  margin-top: 18px;
}
.user-list-panel { padding: 0; overflow: hidden; }
.user-list-heading {
  min-height: 62px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  border-bottom: 1px solid var(--line);
}
.user-list-heading div { display: flex; gap: 12px; align-items: baseline; }
.user-list-heading b { font: 700 20px var(--display); }
.user-list-heading span,
.user-list-heading small { color: var(--muted); font-size: 12px; }
.user-table-wrap { overflow: auto; }
.user-table { min-width: 920px; }
.user-table tbody tr { cursor: pointer; }
.user-table tbody tr:hover,
.user-table tbody tr.is-selected { background: rgba(18, 109, 166, .07); }
.user-table tbody tr.is-selected { box-shadow: inset 4px 0 var(--blue); }
.user-table td b,
.user-table td > span,
.user-table td small { display: block; }
.user-table td small { margin-top: 4px; color: var(--muted); font-size: 12px; }
.role-line { display: flex !important; gap: 4px; flex-wrap: wrap; }
.role-line i {
  padding: 3px 6px;
  color: var(--blue);
  background: rgba(18, 109, 166, .08);
  font-size: 12px;
  font-style: normal;
  font-weight: 800;
}
.state-ok { color: var(--green); font-weight: 800; }
.state-off { color: var(--risk); font-weight: 800; }
/* 「允许登录」「禁止登录」列窄了会拆成两行，四个字必须一起读 */
.login-status { white-space: nowrap; }
.user-pagination {
  min-height: 56px;
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: center;
  border-top: 1px solid var(--line);
}
.user-pagination span { color: var(--muted); font-size: 12px; }
.user-editor {
  position: sticky;
  top: 92px;
  max-height: calc(100vh - 112px);
  overflow: auto;
}
.user-editor header > span {
  color: var(--blue);
  font: 800 12px var(--display);
  letter-spacing: .12em;
}
.user-editor h2 { margin: 6px 0 4px; font: 700 28px var(--display); }
.user-editor header p,
.scope-note,
.password-reset p { color: var(--muted); font-size: 13px; line-height: 1.6; }
.user-editor form { display: grid; gap: 15px; margin-top: 18px; }
.user-editor fieldset {
  min-width: 0;
  margin: 0;
  padding: 14px;
  border: 1px solid var(--line);
}
.user-editor legend {
  padding: 0 7px;
  color: var(--blue);
  font-size: 13px;
  font-weight: 800;
}
.user-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; }
.user-form-grid label,
.wide-field { display: grid; gap: 6px; }
.user-form-grid label > span,
.wide-field > span { color: var(--muted); font-size: 12px; }
.user-form-grid input,
.user-form-grid select,
.wide-field input {
  width: 100%;
  padding: 0 9px;
  border: 1px solid var(--line);
  color: var(--ink);
  background: var(--white);
}
.check-line {
  display: flex !important;
  align-items: center;
  align-self: end;
  min-height: 42px;
  padding: 0 8px;
  border: 1px solid var(--line);
}
.check-line input { width: auto; min-height: 0; }
.role-options,
.project-options { display: grid; gap: 7px; }
.role-options label,
.project-options label {
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: 8px;
  align-items: start;
  padding: 8px;
  border: 1px solid var(--line);
  cursor: pointer;
}
.role-options input,
.project-options input { min-height: 0; margin-top: 3px; }
.role-options span,
.project-options span { display: grid; gap: 3px; }
.role-options small,
.project-options small { color: var(--muted); font-size: 12px; line-height: 1.4; }
.project-options { max-height: 240px; overflow: auto; }
.project-options label.archived { opacity: .58; }
.scope-note { margin: 4px 0; }
.password-reset {
  margin-top: 18px;
  padding: 14px;
  border: 1px solid rgba(196, 63, 56, .28);
  background: rgba(196, 63, 56, .04);
}
.password-reset .reset-toggle { margin-bottom: 0; }
.password-reset .reset-toggle + p { margin-top: 12px; }
.password-reset .wide-field { margin: 12px 0; }
@media (max-width: 1380px) {
  .user-layout { grid-template-columns: 1fr; }
  .user-editor { position: static; max-height: none; }
}
@media (max-width: 700px) {
  .page-heading { display: block; }
  .page-heading h1 { font-size: 36px; }
  .user-head-actions { margin-top: 16px; }
  .user-view-tabs { display: flex; flex-wrap: wrap; border-radius: 12px; }
  .user-view-tabs button { flex: 1 0 auto; white-space: nowrap; }
  /* 手机上入口竖排，但每个仍是一行矮按钮 */
  .people-entry-card { flex-basis: 100%; }
  /* 手机上搜索仍和查询、筛选同一行，用户表先露头 */
  .user-filter { gap: 8px; padding: 12px; }
  .user-filter-panel { grid-template-columns: 1fr 1fr; padding: 12px; }
  .user-form-grid { grid-template-columns: 1fr; }
}
</style>
